<template>
    <div :id="getAccordionHash()" :class="isFaq ? 'border-t border-dashed border-default' : ''">
        <a
            class="flex w-full justify-between items-center py-4"
            :class="isFaq ? 'gap-4' : ''"
            href="#"
            @click.prevent="toggleAccordion()"
            :aria-expanded="isOpen"
            :aria-controls="`content-${effectiveItemId}`"
        >
            <span class="flex flex-col">
                <span v-if="effectiveSubtitle" class="flex text-sm uppercase">{{ effectiveSubtitle }}</span>
                <h4 v-if="effectiveTitle" :class="isFaq ? 'text-lg lg:text-xl font-light text-dark' : 'text-xl lg:text-2xl'">{{ effectiveTitle }}</h4>
            </span>

            <span class="flex justify-center items-center w-11 h-11 min-w-11 rounded-full transition-all duration-200"
                  :class="isFaq ? 'bg-white' : (pageStyle ? `bg-${pageStyle}-primary hover:bg-${pageStyle}-primary-tone` : 'bg-primary hover:bg-primary-tone')"
            >
                <svg
                    class="relative top-px w-4 stroke-dark transition-all duration-200 transform"
                    :class="{
                        'rotate-180': isOpen,
                        'rotate-0': !isOpen,
                    }"
                    fill="none"
                    stroke="currentColor"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 16 10"
                    aria-hidden="true"
                >
                    <path
                        d="M15 1.2l-7 7-7-7"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                    />
                </svg>
            </span>
        </a>

        <div v-if="isFaq"
             class="faq-collapse grid transition-[grid-template-rows] duration-300 ease-out"
             :class="isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'"
        >
            <div class="overflow-hidden" :id="`content-${effectiveItemId}`">
                <div class="pb-4">
                    <slot></slot>
                </div>
            </div>
        </div>

        <div v-else class="border-b border-dotted" :class="pageStyle ? `border-${pageStyle}-primary` : 'border-primary'">
            <Transition>
            <div class="pb-8" v-show="isOpen" :id="`content-${effectiveItemId}`">
                <!-- Slot for content rendered from PHP -->
                <slot></slot>
            </div>
            </Transition>
        </div>
    </div>
</template>

<script>
export default {
    props: {
        title: {
            type: String,
            default: '',
        },
        subtitle: {
            type: String,
            default: '',
        },
        itemId: {
            type: [String, Number],
            required: true,
        },
        index: {
            type: [Number, String],
            default: 0,
        },
        openFirstRow: {
            type: String,
            default: 'no',
        },
        pageStyle: {
            type: String,
            default: '',
        },
        variant: {
            type: String,
            default: '',
        },
        item: {
            type: Object,
            default: null,
        },
    },
    data() {
        return {
            open: false,
        }
    },
    computed: {
        isOpen() {
            return this.open
        },
        isFaq() {
            return this.variant === 'faq'
        },
        // Support legacy item prop for backward compatibility
        effectiveTitle() {
            return this.title || this.item?.title || ''
        },
        effectiveSubtitle() {
            return this.subtitle || this.item?.subtitle || ''
        },
        effectiveItemId() {
            return this.itemId ?? this.item?.id ?? this.index
        },
    },
    methods: {
        getAccordionHash() {
            return `collapse-${this.effectiveItemId}`
        },
        setUrlHash() {
            const hash = this.getAccordionHash()

            if (window.history && window.history.replaceState) {
                window.history.replaceState(null, '', `#${hash}`)
                return
            }

            window.location.hash = hash
        },
        shouldOpenFromHash() {
            const hash = this.getAccordionHash()
            return window.location.hash === `#${hash}`
        },
        handleHashChange() {
            if (this.shouldOpenFromHash()) {
                this.open = true
            }
        },
        toggleAccordion() {
            const willOpen = !this.open
            this.open = willOpen

            if (willOpen) {
                this.setUrlHash()
            }
        },
    },
    mounted() {
        let isOpen = false
        window.addEventListener('hashchange', this.handleHashChange)

        if (this.shouldOpenFromHash()) {
            isOpen = true
        } else if( this.index === 0 ){
            isOpen = this.openFirstRow === 'yes'
        }

        this.open = isOpen
    },
    beforeDestroy() {
        window.removeEventListener('hashchange', this.handleHashChange)
    },
}
</script>

<style scoped>
.v-enter-active,
.v-leave-active {
  transition: opacity 0.5s ease;
}

.v-enter-from,
.v-leave-to {
  opacity: 0;
}
</style>